// Column-filter defaults applied by SbTable so every table in the product
// behaves the same way:
//   1. Every column that has a filter gets the built-in dropdown search.
//   2. Every data column without a filter gets a distinct-value checkbox filter
//      built from the rows currently passed to the table.
//
// Column-level props (stripped before reaching antd):
//   filterable: false          — opt a column out of the auto filter.
//   filterValue(record)        — value to filter on for computed columns (no
//                                dataIndex, or a cell built from several fields).
//   filterLabel(value, record) — label for a value when the raw value differs
//                                from what the cell shows (codes, flags, ...).
// Table-level: <SbTable autoFilters={false}> keeps search-on-existing-filters but
// generates nothing — used by server-paginated tables, where the loaded rows are
// only one page and a client-side filter would silently hide the other pages.

export const BLANK_FILTER_VALUE = "__sb_blank__";
const BLANK_LABEL = "(Blank)";
// Values that render to the same label (e.g. two timestamps on the same day)
// collapse into one option keyed by that label.
const MERGED_PREFIX = "__sb_label__:";

// Blank cells often render a default ("ACTIVE", "General Task"); show that text
// for the blank option. Placeholder renders ("", "—", "-", "N/A") stay "(Blank)".
const PLACEHOLDER_RE = /^(\s*|[-—–]+|n\/?a)$/i;

const getPath = (record, dataIndex) => {
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, key) => (acc == null ? undefined : acc[key]), record);
  }
  return record?.[dataIndex];
};

const hasDataIndex = (dataIndex) =>
  dataIndex !== undefined &&
  dataIndex !== null &&
  dataIndex !== "" &&
  !(Array.isArray(dataIndex) && dataIndex.length === 0);

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const isPrimitive = (value) =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const defaultLabel = (value) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

const walkRows = (rows, childrenColumnName, visit) => {
  (rows || []).forEach((row, index) => {
    visit(row, index);
    const children = row?.[childrenColumnName];
    if (Array.isArray(children)) walkRows(children, childrenColumnName, visit);
  });
};

const tryRender = (column, value, record, index) => {
  try {
    return column.render(value, record, index);
  } catch {
    // A render that can't run outside the table just falls back to the raw value.
    return undefined;
  }
};

// Label for one value: explicit filterLabel, else the cell's render output when
// it is plain text (formatted dates, "L2", ...), else the raw value. render is
// only consulted for plain dataIndex columns — for array cells and filterValue
// columns its first argument is not the value being labelled.
const labelFor = (column, value, record, index, canUseRender) => {
  if (column.filterLabel) return String(column.filterLabel(value, record));
  if (canUseRender && column.render) {
    const rendered = tryRender(column, value, record, index);
    if (typeof rendered === "string" || typeof rendered === "number") return String(rendered);
  }
  return defaultLabel(value);
};

const blankLabelFor = (column, raw, record, index, canUseRender) => {
  if (!canUseRender || !column.render) return BLANK_LABEL;
  const rendered = tryRender(column, raw, record, index);
  if (typeof rendered === "string" && !PLACEHOLDER_RE.test(rendered.trim())) return rendered;
  return BLANK_LABEL;
};

const buildFilter = (column, getValue, rows, childrenColumnName) => {
  const labels = new Map(); // raw value -> label
  let hasBlank = false;
  let blankLabel = BLANK_LABEL;
  let supported = true;

  walkRows(rows, childrenColumnName, (record, index) => {
    if (!supported) return;
    const raw = getValue(record);
    const canUseRender = !column.filterValue && !Array.isArray(raw);
    if (isBlank(raw)) {
      if (!hasBlank) blankLabel = blankLabelFor(column, raw, record, index, canUseRender);
      hasBlank = true;
      return;
    }
    const values = Array.isArray(raw) ? raw : [raw];
    values.forEach((value) => {
      if (isBlank(value)) return;
      if (!isPrimitive(value)) {
        supported = false;
        return;
      }
      if (!labels.has(value)) labels.set(value, labelFor(column, value, record, index, canUseRender));
    });
  });

  if (!supported || labels.size === 0) return null;

  const byLabel = new Map(); // label -> raw values sharing it
  [...labels.keys()].sort(compareValues).forEach((value) => {
    const label = labels.get(value);
    if (!byLabel.has(label)) byLabel.set(label, []);
    byLabel.get(label).push(value);
  });

  const merged = new Map(); // merged option value -> Set of raw values
  const filters = [...byLabel.entries()].map(([label, values]) => {
    if (values.length === 1) return { text: label, value: values[0] };
    const key = `${MERGED_PREFIX}${label}`;
    merged.set(key, new Set(values));
    return { text: label, value: key };
  });
  if (hasBlank) filters.push({ text: blankLabel, value: BLANK_FILTER_VALUE });

  const onFilter = (value, record) => {
    const raw = getValue(record);
    if (value === BLANK_FILTER_VALUE) return isBlank(raw);
    const matches = merged.has(value) ? (v) => merged.get(value).has(v) : (v) => v === value;
    return Array.isArray(raw) ? raw.some(matches) : matches(raw);
  };

  return { filters, onFilter };
};

const applyToColumn = (column, rows, options) => {
  const { filterable, filterLabel, filterValue, ...rest } = column;

  if (Array.isArray(column.children)) {
    return { ...rest, children: column.children.map((c) => applyToColumn(c, rows, options)) };
  }

  if (column.filterDropdown) return rest;

  if (column.filters) {
    return column.filterSearch === undefined ? { ...rest, filterSearch: true } : rest;
  }

  const canFilter = filterValue || hasDataIndex(column.dataIndex);
  if (!options.autoFilters || filterable === false || !canFilter || "onFilter" in column) {
    return rest;
  }

  const getValue = filterValue || ((record) => getPath(record, column.dataIndex));
  const built = buildFilter(column, getValue, rows, options.childrenColumnName);
  if (!built) return rest;

  return { ...rest, ...built, filterSearch: true };
};

export const applyColumnFilters = (columns, rows, options = {}) => {
  if (!Array.isArray(columns)) return columns;
  const resolved = {
    autoFilters: options.autoFilters !== false,
    childrenColumnName: options.childrenColumnName || "children",
  };
  return columns.map((column) => applyToColumn(column, rows, resolved));
};

// ─── Controlled tables ──────────────────────────────────────────────────────
// antd treats filters as controlled for the WHOLE table as soon as any column
// sets `filteredValue`; a filter column without it then never applies (dev-only
// warning). Pages that own filter state (PAIC dashboard, Compensation, ...) wire
// their columns explicitly; SbTable holds state for any filter column they
// didn't, so adding a column to a controlled table can't silently break it.

// Same resolution antd uses for the keys of the `filters` onChange argument.
export const getFilterColumnKey = (column) => {
  if (column.key !== undefined && column.key !== null) return column.key;
  if (!hasDataIndex(column.dataIndex)) return undefined;
  return Array.isArray(column.dataIndex) ? column.dataIndex.join(".") : column.dataIndex;
};

const leafColumns = (columns) =>
  (columns || []).flatMap((c) => (Array.isArray(c.children) ? leafColumns(c.children) : [c]));

export const controlUnwiredFilters = (columns, state) => {
  if (!Array.isArray(columns)) return columns;
  if (!leafColumns(columns).some((c) => "filteredValue" in c)) return columns;

  const wire = (column) => {
    if (Array.isArray(column.children)) return { ...column, children: column.children.map(wire) };
    const isFilter = column.filters || column.filterDropdown || "onFilter" in column;
    const key = getFilterColumnKey(column);
    if (!isFilter || "filteredValue" in column || key === undefined) return column;
    return { ...column, filteredValue: state[key] ?? null };
  };
  return columns.map(wire);
};
